import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  useWhatsappGroups,
  useLastOperationAnalysis,
  useOperationAnalysisHistory,
} from '../hooks/use-whatsapp-groups';
import { Badge } from '@travellegacy/nexops-design-system';
import { Button } from '@travellegacy/nexops-design-system';
import { Input } from '@travellegacy/nexops-design-system';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@travellegacy/nexops-design-system';
import {
  Loader2,
  Search,
  MessageSquare,
  AlertCircle,
  CheckCircle2,
  Clock,
  TrendingUp,
  LayoutDashboard,
  List,
  History,
  ChevronDown,
  ChevronRight,
  TrendingDown,
  Zap,
  Timer
} from 'lucide-react';
import { OperationChat } from '../components/OperationChat';
import { getStatusBadge, getSentimentBadge, getStatusLabel, formatTimeAgo } from '../utils/formatting';

type ViewMode = 'dashboard' | 'all-groups' | 'history';

export default function GroupsListPage() {
  const [viewMode, setViewMode] = useState<ViewMode>('dashboard');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [expandedProblems, setExpandedProblems] = useState<Set<number>>(new Set());

  const { data: groups, isLoading: isLoadingGroups } = useWhatsappGroups({
    search,
    status: statusFilter,
  });

  const { data: analysis, isLoading: isLoadingAnalysis } = useLastOperationAnalysis();
  const { data: history, isLoading: isLoadingHistory } = useOperationAnalysisHistory(10);

  const toggleProblemExpansion = (index: number) => {
    setExpandedProblems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  // Calcular grupos inativos (sem mensagem há mais de 7 dias)
  const getInactiveGroups = () => {
    if (!Array.isArray(groups)) return [];
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    return groups.filter(g => {
      if (!g.lastMessageAt) return true; // Sem mensagens = inativo
      return new Date(g.lastMessageAt) < sevenDaysAgo;
    });
  };

  // Top 5 grupos com mais movimentação
  const getTopActiveGroups = () => {
    if (!Array.isArray(groups)) return [];
    return [...groups]
      .sort((a, b) => (b.messageCount || 0) - (a.messageCount || 0))
      .slice(0, 5);
  };

  // Top 5 grupos com maior SLA (tempo de resposta)
  const getGroupsWithHighestSLA = () => {
    if (!Array.isArray(groups)) return [];
    return [...groups]
      .filter(g => g.avgResponseTime !== null && g.avgResponseTime !== undefined)
      .sort((a, b) => (b.avgResponseTime || 0) - (a.avgResponseTime || 0))
      .slice(0, 5);
  };

  const renderDashboard = () => {
    if (isLoadingAnalysis) {
      return (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      );
    }

    if (!analysis) {
      return (
        <div className="text-center py-12">
          <p className="text-muted-foreground">Nenhuma análise encontrada.</p>
          <p className="text-sm text-muted-foreground mt-2">Execute a automação "Análise Consolidada da Operação" na página de Automações para gerar a primeira análise</p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Top Metrics */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total de Grupos</CardTitle>
              <MessageSquare className="h-4 w-4 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{Array.isArray(groups) ? groups.length : 0}</div>
              <p className="text-xs text-muted-foreground">Grupos monitorados</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Status Geral</CardTitle>
              {analysis.status === 'OK' ? <CheckCircle2 className="h-4 w-4 text-green-500" /> : analysis.status === 'CRITICAL' ? <AlertCircle className="h-4 w-4 text-red-500" /> : <AlertCircle className="h-4 w-4 text-amber-500" />}
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{getStatusLabel(analysis.status)}</div>
              <p className="text-xs text-muted-foreground">Avaliação da operação pela IA</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Nota do Time</CardTitle>
              <TrendingUp className="h-4 w-4 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{analysis.teamScore}/10</div>
              <p className="text-xs text-muted-foreground">Qualidade do atendimento</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total de Mensagens</CardTitle>
              <MessageSquare className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{analysis.totalMessages}</div>
              <p className="text-xs text-muted-foreground mb-2">{analysis.clientMessages} cliente / {analysis.teamMessages} equipe</p>
              {/* Barra de proporção cliente/equipe */}
              <div className="flex h-2 rounded-full overflow-hidden bg-muted">
                <div
                  className="bg-blue-500"
                  style={{ width: `${(analysis.clientMessages / analysis.totalMessages) * 100}%` }}
                  title={`Cliente: ${analysis.clientMessages}`}
                />
                <div
                  className="bg-green-500"
                  style={{ width: `${(analysis.teamMessages / analysis.totalMessages) * 100}%` }}
                  title={`Equipe: ${analysis.teamMessages}`}
                />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">SLA Médio</CardTitle>
              {analysis.avgResponseTime && analysis.avgResponseTime <= 15 ? (
                <Clock className="h-4 w-4 text-green-500" />
              ) : analysis.avgResponseTime && analysis.avgResponseTime <= 30 ? (
                <Clock className="h-4 w-4 text-amber-500" />
              ) : (
                <Clock className="h-4 w-4 text-red-500" />
              )}
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{analysis.avgResponseTime || '--'} min</div>
              <div className="mt-1">
                {analysis.avgResponseTime && analysis.avgResponseTime <= 15 && (
                  <Badge variant="success">Excelente</Badge>
                )}
                {analysis.avgResponseTime && analysis.avgResponseTime > 15 && analysis.avgResponseTime <= 30 && (
                  <Badge variant="warning">Atenção</Badge>
                )}
                {analysis.avgResponseTime && analysis.avgResponseTime > 30 && (
                  <Badge variant="error" className="text-sm font-semibold">CRÍTICO</Badge>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sentimento Geral - Full Width */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Resumo Executivo</CardTitle>
                <CardDescription>Análise geral da operação nos últimos 3 dias</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold">Sentimento:</span>
                {getSentimentBadge(analysis.sentiment)}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm leading-relaxed">
              {analysis.summary}
            </p>
          </CardContent>
        </Card>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-7">
          {/* Tipos de Problemas */}
          <Card className="col-span-4">
            <CardHeader>
              <CardTitle>Tipos de Problemas</CardTitle>
              <CardDescription>Problemas identificados pela IA nos últimos 3 dias</CardDescription>
            </CardHeader>
            <CardContent>
              {analysis.problemTypes && Array.isArray(analysis.problemTypes) && analysis.problemTypes.length > 0 ? (
                <div className="space-y-2">
                  <div className="space-y-2">
                    {analysis.problemTypes.map((pt: { type: string; count: number; description: string; affectedGroups?: Array<{ jid: string; name: string; example: string }> }, i: number) => {
                      const isExpanded = expandedProblems.has(i);
                      const hasAffectedGroups = pt.affectedGroups && pt.affectedGroups.length > 0;

                      return (
                        <div key={i} className="rounded-lg border border-border overflow-hidden">
                          {/* Header - sempre visível */}
                          <button
                            onClick={() => hasAffectedGroups && toggleProblemExpansion(i)}
                            className={`w-full p-3 flex items-center justify-between text-left transition-colors ${hasAffectedGroups ? 'hover:bg-muted/50 cursor-pointer' : 'cursor-default'}`}
                            disabled={!hasAffectedGroups}
                          >
                            <div className="flex items-center gap-2 flex-1">
                              {hasAffectedGroups && (
                                isExpanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />
                              )}
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="font-semibold text-sm">{pt.type}</span>
                                  <Badge variant="outline" className="text-xs">{pt.count}</Badge>
                                </div>
                                <p className="text-xs text-muted-foreground">{pt.description}</p>
                              </div>
                            </div>
                          </button>

                          {/* Grupos afetados - expansível */}
                          {isExpanded && hasAffectedGroups && (
                            <div className="border-t bg-muted/30 p-3 space-y-2">
                              <p className="text-xs font-semibold text-muted-foreground mb-2">Grupos Afetados:</p>
                              {pt.affectedGroups!.map((group, gIdx) => (
                                <Link
                                  key={gIdx}
                                  to={`/whatsapp/groups/${group.jid}`}
                                  className="block p-2 rounded bg-background border border-border hover:border-primary transition-colors"
                                >
                                  <div className="flex items-start justify-between gap-2 mb-1">
                                    <span className="font-semibold text-xs">{group.name}</span>
                                    <MessageSquare className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                                  </div>
                                  <p className="text-xs text-muted-foreground italic">{group.example}</p>
                                </Link>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <CheckCircle2 className="h-8 w-8 text-green-500 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">Nenhum problema identificado!</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Grupos Críticos e Atenção */}
          <Card className="col-span-3">
            <CardHeader>
              <CardTitle>Grupos Críticos e Atenção</CardTitle>
              <CardDescription>Grupos que precisam de ação imediata</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {(!analysis.rankings?.criticalGroups || analysis.rankings.criticalGroups.length === 0) &&
               (!analysis.rankings?.attentionGroups || analysis.rankings.attentionGroups.length === 0) ? (
                <div className="text-center py-8">
                  <CheckCircle2 className="h-8 w-8 text-green-500 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">Tudo em dia! Nenhum grupo crítico.</p>
                </div>
              ) : (
                <>
                  {analysis.rankings?.criticalGroups?.map((g: { jid: string; name: string; reason: string }, i: number) => (
                    <Link key={i} to={`/whatsapp/groups/${g.jid}`} className="block p-3 rounded-lg border border-red-200 bg-red-50 hover:bg-red-100 transition-colors">
                      <div className="flex justify-between items-start mb-1">
                        <span className="font-semibold text-sm text-red-700">{g.name}</span>
                        <Badge variant="error" className="text-[10px] h-4">CRÍTICO</Badge>
                      </div>
                      <p className="text-xs text-red-600">{g.reason}</p>
                    </Link>
                  ))}

                  {analysis.rankings?.attentionGroups?.map((g: { jid: string; name: string; reason: string }, i: number) => (
                    <Link key={i} to={`/whatsapp/groups/${g.jid}`} className="block p-3 rounded-lg border border-amber-200 bg-amber-50 hover:bg-amber-100 transition-colors">
                      <div className="flex justify-between items-start mb-1">
                        <span className="font-semibold text-sm text-amber-700">{g.name}</span>
                        <Badge variant="warning" className="text-[10px] h-4">ATENÇÃO</Badge>
                      </div>
                      <p className="text-xs text-amber-600">{g.reason}</p>
                    </Link>
                  ))}
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Grupos Destaque - Full Width */}
        {analysis.rankings?.topPerformers && analysis.rankings.topPerformers.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-green-700 flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5" />
                Grupos Destaque
              </CardTitle>
              <CardDescription>Grupos com excelente desempenho no atendimento</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                {analysis.rankings.topPerformers.map((g: { jid: string; name: string; reason: string }, i: number) => (
                  <Link
                    key={i}
                    to={`/whatsapp/groups/${g.jid}`}
                    className="block p-4 rounded-lg border-2 border-green-200 bg-green-50 hover:bg-green-100 hover:border-green-300 transition-all"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <span className="font-semibold text-sm text-green-800">{g.name}</span>
                      <Badge variant="success" className="text-[10px] h-5">🌟 DESTAQUE</Badge>
                    </div>
                    <p className="text-xs text-green-700 leading-relaxed">{g.reason}</p>
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Rankings de Grupos */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {/* Top 5 Grupos Ativos */}
          <Card>
            <CardHeader>
              <CardTitle className="text-blue-700 flex items-center gap-2">
                <Zap className="h-5 w-5" />
                Top 5 Mais Ativos
              </CardTitle>
              <CardDescription>Grupos com maior movimentação</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {getTopActiveGroups().length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-4">Nenhum grupo ativo</p>
                ) : (
                  getTopActiveGroups().map((group, idx) => (
                    <Link
                      key={group.id}
                      to={`/whatsapp/groups/${group.groupJid}`}
                      className="block p-2 rounded-lg border hover:border-primary transition-colors"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-lg text-muted-foreground">#{idx + 1}</span>
                          <span className="font-semibold text-sm truncate">{group.name}</span>
                        </div>
                        <Badge variant="outline" className="text-xs">{group.messageCount || 0}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">{formatTimeAgo(group.lastMessageAt)}</p>
                    </Link>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          {/* Top 5 Resposta Lenta */}
          <Card>
            <CardHeader>
              <CardTitle className="text-red-700 flex items-center gap-2">
                <Timer className="h-5 w-5" />
                Top 5 Resposta Lenta
              </CardTitle>
              <CardDescription>Grupos com maior tempo de resposta</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {(() => {
                  const highSLAGroups = getGroupsWithHighestSLA();
                  if (highSLAGroups.length === 0) {
                    return (
                      <p className="text-xs text-muted-foreground text-center py-4">
                        Nenhum grupo com dados de SLA
                      </p>
                    );
                  }

                  return highSLAGroups.map((group, idx) => {
                    const slaMinutes = group.avgResponseTime || 0;
                    const slaHours = Math.floor(slaMinutes / 60);
                    const slaRemainingMinutes = slaMinutes % 60;
                    const slaText = slaHours > 0
                      ? `${slaHours}h ${slaRemainingMinutes}min`
                      : `${slaMinutes}min`;

                    return (
                      <Link
                        key={group.id}
                        to={`/whatsapp/groups/${group.groupJid}`}
                        className="block p-2 rounded-lg border hover:border-primary transition-colors"
                      >
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-lg text-muted-foreground">#{idx + 1}</span>
                            <span className="font-semibold text-sm truncate">{group.name}</span>
                          </div>
                          <Badge
                            variant={slaMinutes > 60 ? 'error' : slaMinutes > 30 ? 'warning' : 'default'}
                            className="text-xs"
                          >
                            {slaText}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">{formatTimeAgo(group.lastMessageAt)}</p>
                      </Link>
                    );
                  });
                })()}
              </div>
            </CardContent>
          </Card>

          {/* Grupos Inativos */}
          <Card>
            <CardHeader>
              <CardTitle className="text-amber-700 flex items-center gap-2">
                <TrendingDown className="h-5 w-5" />
                Grupos Inativos
              </CardTitle>
              <CardDescription>Sem movimentação há mais de 7 dias</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {getInactiveGroups().length === 0 ? (
                  <div className="text-center py-4">
                    <CheckCircle2 className="h-6 w-6 text-green-500 mx-auto mb-1" />
                    <p className="text-xs text-muted-foreground">Todos os grupos ativos!</p>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between mb-3">
                      <Badge variant="warning" className="text-xs">{getInactiveGroups().length} grupos inativos</Badge>
                    </div>
                    <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
                      {getInactiveGroups().map((group) => {
                        const daysSince = group.lastMessageAt
                          ? Math.floor((Date.now() - new Date(group.lastMessageAt).getTime()) / (1000 * 60 * 60 * 24))
                          : 999;

                        return (
                          <Link
                            key={group.id}
                            to={`/whatsapp/groups/${group.groupJid}`}
                            className="block p-2.5 rounded-lg border hover:border-amber-400 transition-all bg-card"
                          >
                            <div className="flex items-center justify-between gap-2">
                              <span className="font-medium text-sm truncate flex-1">{group.name}</span>
                              <Badge variant="outline" className="text-[10px] whitespace-nowrap shrink-0">
                                {daysSince}d inativo
                              </Badge>
                            </div>
                            {group.clientName && (
                              <p className="text-xs text-muted-foreground mt-0.5 truncate">
                                {group.clientName}
                              </p>
                            )}
                          </Link>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Performance do Time */}
        {analysis.metrics?.teamPerformance && (
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-green-700 flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5" />
                  Destaques da Equipe
                </CardTitle>
                <CardDescription>Conquistas e pontos fortes nos últimos 3 dias</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {analysis.metrics.teamPerformance.highlights.map((highlight: string, i: number) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span>{highlight}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-amber-700 flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Oportunidades de Melhoria
                </CardTitle>
                <CardDescription>Pontos de atenção para aprimorar o atendimento</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {analysis.metrics.teamPerformance.improvements.map((improvement: string, i: number) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                      <span>{improvement}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    );
  };

  // Ordenar grupos por prioridade de status
  const getSortedGroups = () => {
    if (!Array.isArray(groups)) return [];

    const statusPriority: Record<string, number> = {
      'AWAITING_REPLY': 1,
      'CRITICAL': 2,
      'INACTIVE': 3,
      'OK': 4,
    };

    return [...groups].sort((a, b) => {
      const priorityA = statusPriority[a.status || 'OK'] || 5;
      const priorityB = statusPriority[b.status || 'OK'] || 5;
      return priorityA - priorityB;
    });
  };

  const renderAllGroups = () => {
    return (
      <div className="space-y-4">
        <div className="flex gap-4 items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar grupos..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>

          <div className="flex gap-2">
            <Button
              variant={!statusFilter ? 'default' : 'outline'}
              onClick={() => setStatusFilter(undefined)}
              size="sm"
            >
              Todos
            </Button>
            <Button
              variant={statusFilter === 'AWAITING_REPLY' ? 'default' : 'outline'}
              onClick={() => setStatusFilter('AWAITING_REPLY')}
              size="sm"
            >
              Aguardando
            </Button>
            <Button
              variant={statusFilter === 'CRITICAL' ? 'default' : 'outline'}
              onClick={() => setStatusFilter('CRITICAL')}
              size="sm"
            >
              Críticos
            </Button>
          </div>
        </div>

        {isLoadingGroups ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : (
          <Card>
            <CardContent className="p-0">
              <div className="divide-y">
                {getSortedGroups().map((group) => (
                  <Link
                    key={group.id}
                    to={`/whatsapp/groups/${group.groupJid}`}
                    className="flex items-center justify-between p-4 hover:bg-muted/50 transition-colors"
                  >
                    {/* Nome e Status */}
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-sm truncate">{group.name}</h3>
                        {group.clientName && (
                          <p className="text-xs text-muted-foreground truncate">{group.clientName}</p>
                        )}
                      </div>
                    </div>

                    {/* Métricas */}
                    <div className="flex items-center gap-6">
                      {/* Mensagens */}
                      <div className="hidden md:flex items-center gap-2">
                        <MessageSquare className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm font-medium">{group.messageCount || 0}</span>
                      </div>

                      {/* Última mensagem */}
                      <div className="hidden sm:flex items-center gap-2">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {formatTimeAgo(group.lastMessageAt)}
                        </span>
                      </div>

                      {/* Status Badge */}
                      <div className="min-w-[100px] flex justify-end">
                        {getStatusBadge(group.status)}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {!isLoadingGroups && getSortedGroups().length === 0 && (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Nenhum grupo encontrado</p>
          </div>
        )}
      </div>
    );
  };

  const renderHistory = () => {
    if (isLoadingHistory) {
      return (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {history?.map((h) => (
          <Card key={h.id} className="overflow-hidden">
             <CardHeader className="p-4 bg-muted/30">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <CardTitle className="text-sm font-bold">
                      Relatório gerado em {new Date(h.createdAt).toLocaleDateString('pt-BR')} às {new Date(h.createdAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                    </CardTitle>
                    {getStatusBadge(h.status)}
                    {getSentimentBadge(h.sentiment)}
                  </div>
                  <span className="text-xs font-bold">Nota: {h.teamScore}/10</span>
                </div>
             </CardHeader>
             <CardContent className="p-4">
                <p className="text-sm text-muted-foreground line-clamp-2">{h.summary}</p>
                <div className="mt-2 flex gap-4 text-xs text-muted-foreground">
                  <span>{h.totalMessages} mensagens</span>
                  <span>{h.avgResponseTime} min SLA</span>
                </div>
             </CardContent>
          </Card>
        ))}
      </div>
    );
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold">WhatsApp Operação</h1>
        <p className="text-muted-foreground">
          Monitoramento de atendimento e qualidade da operação
        </p>
      </div>

      <div className="flex border-b">
        <button
          className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${viewMode === 'dashboard' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          onClick={() => setViewMode('dashboard')}
        >
          <LayoutDashboard className="inline h-4 w-4 mr-2" />
          Dashboard
        </button>
        <button
          className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${viewMode === 'all-groups' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          onClick={() => setViewMode('all-groups')}
        >
          <List className="inline h-4 w-4 mr-2" />
          Todos os Grupos
        </button>
        <button
          className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${viewMode === 'history' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          onClick={() => setViewMode('history')}
        >
          <History className="inline h-4 w-4 mr-2" />
          Execuções IA
        </button>
      </div>

      {viewMode === 'dashboard' && renderDashboard()}
      {viewMode === 'all-groups' && renderAllGroups()}
      {viewMode === 'history' && renderHistory()}

      {/* Chat Consultivo Flutuante */}
      <OperationChat />
    </div>
  );
}
